import get from 'lodash/get'
import merge from 'lodash/merge'
import omitBy from 'lodash/omitBy'
import { constants, models } from '@jessicarobins/lineaday'

import router from '@/router'
import firebase, { functions, plugins } from '@/firebase'
import bugsnagClient from '@/bugsnag'
import {
  displayError, displayMessage, getLineFromStorage
} from '@/util'

import userApi from '@/api/user'

const initialState = {
  blockedInBrowser: false,
  encryptionKey: null,
  loading: 0,
  settings: {
    imageCount: 0,
    lastPostDate: null,
    lastSeenAt: null,
    promptType: models.prompt.PromptType.DEFAULT,
    reminderTime: constants.DEFAULT_REMINDER_TIME,
    sendNotifications: true,
  },
  user: null
}

const getLoginMethod = () => {
  const { currentUser } = firebase.auth()
  if (currentUser) {
    const { providerData } = currentUser
    const { providerId } = providerData[0]
    return providerId
  }
  return null
}

const actions = {
  async createEncryptionKey({ commit }, { user }) {
    console.log('Creating encryption key for user ', user.uid)
    const idToken = await user.getIdToken()
    const { data } = await functions.setEncryptionKey({ idToken })
    firebase.auth().currentUser.getIdToken(true)
    commit('setEncryptionKey', data.encryptionKey)
  },
  async reauthenticateAndDeleteUser({ dispatch }, password) {
    const { currentUser } = firebase.auth()
    const providerId = getLoginMethod()
    if (providerId === 'password') {
      // Email & password
      const credential = firebase.auth.EmailAuthProvider.credential(currentUser.email, password)
      await currentUser.reauthenticateWithCredential(credential)
    } else {
      // Google
      await currentUser.reauthenticateWithPopup(new firebase.auth.GoogleAuthProvider())
    }
    await dispatch('deleteUser')
  },
  async deleteUser({ commit }) {
    await firebase.auth().currentUser.delete()
    commit('resetUser')
    commit('resetLines')
    commit('toggleNotificationBanner', false)
    router.push('/login')
  },
  async onUserLogin({ commit, dispatch, state }, { user, line = getLineFromStorage() }) {
    commit('incrementUserLoading')

    try {
      commit('setUser', user)
      const hasEncryptionKey = await dispatch('getEncryptionKey')
      if (!hasEncryptionKey) {
        await dispatch('onUserSignUp', { user })
      } else {
        await dispatch('getUserSettings')
        // user hasn't explicitly turned off notifications
        if (state.settings.sendNotifications === true) {
          dispatch('requestMessagingPermission')
        } else if (state.settings.sendNotifications === undefined) {
          commit('toggleNotificationBanner', true)
        }

        dispatch('getSubscription')
      }

      if (line) {
        await dispatch('addLine', line)
      }
    } catch (err) {
      bugsnagClient.notify(err)
      displayError(err)
    } finally {
      commit('decrementUserLoading')
    }
  },
  async onUserSignUp({ commit, dispatch }, { user, line = getLineFromStorage() }) {
    await dispatch('createEncryptionKey', { user })
    commit('setUser', user)
    commit('toggleNotificationBanner', true)
    dispatch('updateUser', { reminderTime: constants.DEFAULT_REMINDER_TIME })
    commit('setSubscriptionLoading', false)

    if (line) {
      await dispatch('addLine', line)
    }
  },
  async getEncryptionKey({ commit }) {
    const { claims: { encryptionKey } } = await firebase.auth().currentUser.getIdTokenResult()
    if (!encryptionKey) {
      return false
    }
    commit('setEncryptionKey', encryptionKey)
    return true
  },
  async getUserSettings({ commit }) {
    const settings = await userApi.getUserSettings()

    if (settings) {
      commit('modifyUserSettings', settings)
    }
  },
  async requestMessagingPermission({ commit, dispatch }, { notify = false } = {}) {
    try {
      await Notification.requestPermission()
      dispatch('setMessagingToken')
      plugins.messaging.onTokenRefresh(() => {
        dispatch('setMessagingToken')
      })

      if (notify) {
        displayMessage('Successfully subscribed to notifications!')
      }
    } catch (err) {
      commit('setBlockedInBrowser', true)
      console.log('Unable to get permission to notify.', err)
    }
  },
  async setMessagingToken({ dispatch }) {
    let messagingTokens = null
    try {
      const messagingToken = await plugins.messaging.getToken()
      messagingTokens = { [messagingToken]: true }
    } catch (err) {
      console.log('Unable to get permission to notify.', err)
      displayError(err)
    }
    dispatch('updateUser', {
      messagingTokens,
      sendNotifications: !!messagingTokens
    })
  },
  async updateUser({ commit }, attributes) {
    const nonEmptyAttributes = omitBy(attributes, attribute => attribute === undefined)
    await userApi.updateUserSettings(nonEmptyAttributes)

    commit('modifyUserSettings', nonEmptyAttributes)
  },
  async updateUserSettings({ commit, dispatch }, {
    lastSeenAt, sendNotifications, promptType, reminderTime, silent
  }) {
    if (!silent) {
      commit('incrementUserLoading')
    }
    try {
      await dispatch('updateUser', {
        lastSeenAt, promptType, sendNotifications, reminderTime
      })
      if (!silent) {
        displayMessage('Notification settings updated!')
      }
    } catch (err) {
      bugsnagClient.notify(err)
      displayError(err)
    }
    commit('decrementUserLoading')
  },
  async userGoogleSignIn({ commit, dispatch }, { isMobile, line }) {
    commit('incrementUserLoading')
    try {
      const provider = new firebase.auth.GoogleAuthProvider()

      if (isMobile) {
        console.log('User is mobile. Signing in with redirect')
        if (line) {
          localStorage.setItem('line', JSON.stringify(line))
        }
        firebase.auth().signInWithRedirect(provider)
        firebase.auth().getRedirectResult()
      } else {
        console.log('User is not mobile. Signing in with popup')
        const { user, additionalUserInfo } = await firebase.auth().signInWithPopup(provider)
        const dispatched = additionalUserInfo.isNewUser ? 'userSignUp' : 'userLogin'
        console.log('User is new: ', additionalUserInfo.isNewUser)
        await dispatch(dispatched, { user, line })
      }
    } catch (err) {
      bugsnagClient.notify(err)
      displayError(err)
    }
    commit('decrementUserLoading')
  },
  async userEmailSignIn({ commit, dispatch }, { user: { email, password }, line }) {
    commit('incrementUserLoading')
    try {
      const doc = await firebase.auth().signInWithEmailAndPassword(email, password)
      await dispatch('userLogin', { user: doc.user, line })
    } catch (err) {
      bugsnagClient.notify(err)
      displayError(err)
    }
    commit('decrementUserLoading')
  },
  async userEmailSignUp({ commit, dispatch }, { user: { email, password }, line }) {
    commit('incrementUserLoading')
    try {
      const doc = await firebase.auth().createUserWithEmailAndPassword(email, password)
      await dispatch('userSignUp', { user: doc.user, line })
    } catch (err) {
      bugsnagClient.notify(err)
      displayError(err)
    }
    commit('decrementUserLoading')
  },
  async userLogin({ dispatch }, { user, line }) {
    await dispatch('onUserLogin', { user, line })
    router.push('/home')
  },
  async userSignUp({ dispatch }, { user, line }) {
    await dispatch('onUserSignUp', { user, line })
    router.push('/home')
  },
  async userSignOut({ commit }) {
    await firebase.auth().signOut()
    commit('resetUser')
    commit('resetLines')
    commit('resetSubscription')
    commit('toggleNotificationBanner', false)
    router.push('/')
  },
}

const getters = {
  blockedInBrowser: state => state.blockedInBrowser,
  encryptionKey: state => state.encryptionKey,
  isAuthenticated: state => !!state.user,
  userEmail: state => get(state, 'user.email', ''),
  userId: state => get(state, 'user.uid'),
  userIsLoading: state => state.loading > 0,
  userLastSeenAt: state => state.settings.lastSeenAt
    || (state.settings.lastPostDate && state.settings.lastPostDate.toDate()),
  userLoginMethod: () => getLoginMethod(),
  userSettings: state => state.settings,
}

const mutations = {
  modifyUserSettings(state, {
    imageCount, lastSeenAt, lastPostDate, promptType, reminderTime, sendNotifications
  }) {
    merge(state.settings, {
      imageCount, lastSeenAt, lastPostDate, promptType, reminderTime, sendNotifications
    })
  },
  resetUser(state) {
    state.blockedInBrowser = false
    state.encryptionKey = null
    state.settings = {}
    state.user = null
  },
  setBlockedInBrowser(state, blockedInBrowser) {
    state.blockedInBrowser = blockedInBrowser
  },
  setEncryptionKey(state, key) {
    state.encryptionKey = key
  },
  incrementUserLoading(state) {
    state.loading += 1
  },
  decrementUserLoading(state) {
    state.loading -= 1
  },
  setUser(state, { uid, email }) {
    bugsnagClient.user = { uid, email }
    state.user = { uid, email }
  }
}

export default {
  state: initialState,
  actions,
  getters,
  mutations
}
